import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getLoginToken, getStoredUser, setStoredUser } from "../storage";
import { getDecodedJWT } from "../utils";
import { useAuthenticatedUser } from "./hooks";
import SessionTimeoutModal from "../modules/Timeout";
import type { ChildProps, userProps } from "../interface";
import { hooks } from "../hooks";

export interface AuthContextType {
  user?: userProps;
  token?: string;
  isAuthenticated: boolean;
  authenticate(token: string): void;
  logout(reason?: "manual" | "expired"): void;
  updateUser(data: userProps): void;
  toggleDrawer(): void;
  open: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: undefined,
  token: undefined,
  isAuthenticated: false,
  authenticate: () => {},
  logout: () => {},
  updateUser: () => {},
  toggleDrawer: () => {},
  open: false,
});

const TIMEOUT_WARNING_SECONDS = 120;
const HOME_URL = "/"; // change if your home route differs

function AuthContextProvider({ children }: ChildProps) {
  const queryClient = useQueryClient();
  const userDetails = useAuthenticatedUser();
  const { mutate } = hooks.useExtendSession();

  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<userProps | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  // Keep window.setTimeout ids
  const timeoutTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);

  // ---- helpers ----
  const clearTimer = (ref: React.MutableRefObject<number | null>) => {
    if (ref.current !== null) {
      window.clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const clearAllTimers = useCallback(() => {
    clearTimer(timeoutTimerRef);
    clearTimer(warningTimerRef);
  }, []);

  const redirectHome = () => {
    // Use replace to prevent "Back" into an expired state
    window.location.replace(HOME_URL);
  };

  const hardResetState = () => {
    setAuthToken(undefined);
    setUser(undefined);
    queryClient.clear();
  };

  const logout = useCallback(
    (reason: "manual" | "expired" = "manual") => {
      clearAllTimers();
      try {
        // Wipe client state
        localStorage.clear();
        hardResetState();
      } finally {
        // Always send user home (also on expiry)
        redirectHome();
      }
    },
    [clearAllTimers, queryClient]
  );

  // ---- initial load from storage ----
  useEffect(() => {
    const token = getLoginToken();
    if (token) setAuthToken(token);

    const stored = getStoredUser();
    if (stored) setUser(stored);
  }, []);

  // ---- keep user in sync w/ freshest profile ----
  useEffect(() => {
    if (userDetails) setUser(userDetails);
  }, [userDetails]);

  // ---- schedule warning + expiry based on JWT exp ----
  useEffect(() => {
    clearAllTimers();

    if (!authToken) {
      // No token → ensure we’re logged out & home
      return;
    }

    const decoded: any = getDecodedJWT(); // expects seconds epoch in exp
    const nowSec = Date.now() / 1000;
    const expSec = Number(decoded?.exp ?? 0);
    const expiresInSec = Math.max(0, expSec - nowSec);

    if (!expSec || expiresInSec <= 0) {
      // Already expired
      logout("expired");
      return;
    }

    // Show warning a minute before expiry (or immediately if < 60s left)
    const warnInMs = Math.max(
      0,
      (expiresInSec - TIMEOUT_WARNING_SECONDS) * 1000
    );
    warningTimerRef.current = window.setTimeout(() => {
      setShowTimeoutModal(true);
    }, warnInMs);

    // Hard expiry → force logout + redirect
    timeoutTimerRef.current = window.setTimeout(() => {
      logout("expired");
    }, expiresInSec * 1000);

    return () => clearAllTimers();
  }, [authToken, logout, clearAllTimers]);

  // ---- public API ----
  function updateUser(data: userProps) {
    setUser(data);
    setStoredUser(data);
  }

  function authenticate(token: string) {
    // Persist the token (use your helper if available)
    try {
      localStorage.setItem("jwt", token);
    } catch {}
    setAuthToken(token);

    const decoded: any = getDecodedJWT();
    const u: userProps = {
      _id: decoded?._id || decoded?.user?._id || "",
      name: decoded?.name || decoded?.user?.name || "",
      fullname: "",
      email: decoded?.user?.email || decoded?.email || "",
      roles: decoded?.user?.roles || decoded?.roles || "",
      isActive: !!(decoded?.user?.isActive ?? decoded?.isActive),
    };

    setUser(u);
    setStoredUser(u);
  }

  function toggleDrawer() {
    setOpen((o) => !o);
  }

  const handleModalLogout = () => {
    setShowTimeoutModal(false);
    logout("expired");
  };

  const handleExtend = () => {
    setShowTimeoutModal(false);
    clearAllTimers();
    // Server should refresh the session/JWT; on success your token hook should update
    mutate();
  };

  const value: AuthContextType = {
    user,
    token: authToken,
    isAuthenticated: Boolean(authToken),
    authenticate,
    logout,
    updateUser,
    toggleDrawer,
    open,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {Boolean(authToken) && (
        <SessionTimeoutModal
          visible={showTimeoutModal}
          secondsLeft={TIMEOUT_WARNING_SECONDS}
          onExtend={handleExtend}
          onLogout={handleModalLogout}
        />
      )}
    </AuthContext.Provider>
  );
}

export default AuthContextProvider;
